import { useField } from 'formik';
import React from 'react';
import MaskedInput from 'react-text-mask';
import type { Mask as MaskArray } from 'react-text-mask';
import {
  GridProps,
  TextFieldProps,
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Typography,
  TextField,
  InputLabel,
  Tooltip,
  Grid,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSchemaContext } from './SparkForm';
import { DropdownOption } from 'types/common/DropdownOption';
import { useResize } from 'hooks/common';
import { SparkImageLoader } from 'sharedComponents/UI';
type Mask = MaskArray | ((value: string) => MaskArray);

const SelectCountry = ({
  countriesOptions,
  onChange,
}: {
  countriesOptions: DropdownOption[];
  onChange: (countryID: string) => void;
}) => {
  return (
    <Select
      sx={{
        borderRight: (theme) => `1px solid ${theme.palette.borderColor}`,
        marginRight: 2,
        padding: 0,
        '& div': {
          paddingLeft: 0,
        },
        '& fieldset': {
          borderColor: '#EEEEEE',
          borderRadius: '10px',
          display: 'none',
        },
      }}
      displayEmpty={false}
      onChange={(e) => onChange(e.target.value as string)}
      defaultValue={
        (countriesOptions.find((option) => option.label === '+1')
          ?.value as string) || ''
      }
      renderValue={(value) => {
        const option = countriesOptions.find(
          (option) => option.value === value
        );
        return (
          <Box display='flex' alignItems='center' gap={2}>
            {!!option?.iconUrl && (
              <SparkImageLoader
                style={{ width: 24, aspectRatio: 2 / 1 }}
                src={option?.iconUrl}
                alt={option?.iconUrl}
              />
            )}
            <Typography>{option?.label}</Typography>
          </Box>
        );
      }}
    >
      {countriesOptions.map((option) => (
        <MenuItem key={option.value} value={option.value} dense divider>
          <Box display='flex' flexDirection='column' p={0.5}>
            <Box display='flex' alignItems='center' gap={2}>
              {!!option.iconUrl && (
                <SparkImageLoader
                  style={{ width: 24, aspectRatio: 2 / 1 }}
                  src={option.iconUrl}
                  alt={option.iconUrl}
                  loading='lazy'
                />
              )}
              <Typography fontFamily='Mont-semibold' fontSize={14}>
                {option.label}
              </Typography>
            </Box>
            {!!option.description && (
              <Typography
                fontSize={14}
                sx={{
                  textWrap: 'wrap',
                  color: '#4A4A4A',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  lineHeight: 'normal',
                }}
              >
                {option.description}
              </Typography>
            )}
          </Box>
        </MenuItem>
      ))}
    </Select>
  );
};

type TextFieldMaskProps = React.HTMLAttributes<HTMLInputElement> & {
  inputRef?: (ref: HTMLElement | null) => void;
  mask?: Mask;
};
function TextFieldMask({
  inputRef,
  mask,
  ...rest
}: TextFieldMaskProps): React.ReactElement {
  return (
    <MaskedInput
      {...rest}
      ref={(ref) => {
        inputRef && inputRef(ref ? ref.inputElement : null);
      }}
      mask={[
        '(',
        /[1-9]/,
        /\d/,
        /\d/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
      ]}
      placeholderChar={'\u2000'}
      showMask={false}
    />
  );
}

export type SparkFormMaskedTextFieldProps = {
  name: string;
  label: string | React.ReactNode;
  placeholder?: string;
  size?: GridProps['xs'];
  readonly?: boolean;
  helpText?: string;
  subHeaderText?: string;
  countriesOptions?: DropdownOption[] | undefined;
} & Omit<TextFieldProps, 'sx' | 'children' | 'size'>;

function SparkFormPhoneInput({
  name,
  label,
  placeholder,
  size,
  onChange,
  type = 'text',
  subHeaderText,
  helpText,
  countriesOptions,
}: SparkFormMaskedTextFieldProps): React.ReactElement {
  const [field, meta, helpers] = useField<string>(name);
  const [selectedCountry, setSelectedCountry] = React.useState<string>('');
  const selectedCountryCode = countriesOptions?.find(
    (option) => option.value === selectedCountry
  );
  const { isMobile } = useResize();
  const { validationSchema } = useSchemaContext();
  const isFieldRequired = React.useMemo(() => {
    try {
      const fieldSchema = validationSchema.describe().fields[name];
      // @ts-ignore
      const isRequiredSchema = fieldSchema.tests.find(
        //@ts-ignore
        (test) => test.name === 'requiredTest'
      );
      if (isRequiredSchema) {
        return true;
      }

      if ('optional' in fieldSchema) {
        return !fieldSchema.optional;
      }
      return false;
    } catch {
      return false;
    }
  }, [name, validationSchema]);

  const isError = meta.touched && !!meta.error;
  const defaultSize = size || (isMobile ? 12 : 6);
  const handleChangeStripNonNumeric = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange && onChange(event);
    helpers.setValue(event.target.value.replace(/[^\d]/g, ''));
  };
  return (
    <Grid item xs={defaultSize}>
      <InputLabel
        sx={{ marginLeft: 1, display: 'flex', gap: 0.5, fontSize: 16 }}
      >
        <span>{isFieldRequired && '*'}</span>
        {label}
        {helpText && (
          <Tooltip title={helpText} placement='top' arrow>
            <InfoOutlinedIcon fontSize='small' />
          </Tooltip>
        )}
      </InputLabel>
      {subHeaderText && (
        <Typography color='#A3A3A3' marginLeft={1.6} fontSize={12}>
          {subHeaderText}
        </Typography>
      )}
      <FormControl
        variant='outlined'
        error={meta.touched && meta.error ? true : false}
        sx={{ width: '100%' }}
      >
        <Box display='flex' flexWrap='nowrap'>
          <TextField
            type={type}
            style={{ border: 'unset' }}
            placeholder={placeholder || ''}
            fullWidth
            error={isError}
            autoComplete='off'
            InputProps={{
              startAdornment: (
                <SelectCountry
                  countriesOptions={countriesOptions || []}
                  onChange={setSelectedCountry}
                />
              ),
              inputComponent: TextFieldMask,
            }}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              '& .MuiInputBase-multiline': {
                alignItems: 'flex-start',
              },
              '& fieldset': {
                borderColor: '#EEEEEE',
                borderRadius: '10px',
              },
            }}
            {...field}
            onChange={handleChangeStripNonNumeric}
          />
        </Box>

        <FormHelperText sx={{ marginBottom: 0.5 }}>
          {isError ? meta.error : ' '}
        </FormHelperText>
      </FormControl>
    </Grid>
  );
}

export default SparkFormPhoneInput;
